import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AlertModule } from '@core/components/alert/alert.module';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { getMe } from '@store/reducers/profile.reducer';
import { currencies, CurrencyEnum, User } from 'lingo2-models';
import { OnUiButtonModule, OnUiButtonState } from 'onclass-ui';
import { takeUntil, tap } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { AccountService, PlatformService } from '../../services';
import { CurrencyNotifierService } from './currency-notifier.service';

@Component({
  selector: 'app-currency-notifier',
  standalone: true,
  imports: [CommonModule, OnUiButtonModule, TranslateModule, AlertModule, IconComponent],
  templateUrl: './currency-notifier.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyNotifierComponent extends ChangableComponent implements OnInit {
  public alertVisible = false;
  public queryCurrency: string;
  public myCurrency: string;
  public me: User;
  public saveState: OnUiButtonState = 'default';
  public hideModal: boolean;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    private accountService: AccountService,
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private currencyNotifierService: CurrencyNotifierService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit(): void {
    this.accountService.hideCurrencyModal$.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
      this.hideModal = data;
      this.detectChanges();
    });

    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          if (me?.currency_id) {
            this.myCurrency =
            Object.keys(CurrencyEnum)[Object.values(CurrencyEnum).indexOf(me?.currency_id)]?.toUpperCase();
          }
          if (this.myCurrency === 'RUR') {
            this.myCurrency = 'RUB';
          }
          this.myQueryParamsWithCurrency();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PROFILE-PROBLEM');
        },
      });
  }

  public myQueryParamsWithCurrency() {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      if (params?.currency && this.myCurrency && (params?.currency).toUpperCase() !== this.myCurrency.toUpperCase()) {
        this.queryCurrency = params?.currency;
        this.alertVisible = true;
      }
      this.detectChanges();
    });
  }

  public setCurrency(currency: string) {
    const queryParams: Params = { currency };

    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      })
      .catch();
  }

  public changeCurrency() {
    const currency_id = currencies[CurrencyEnum[this.queryCurrency.toLowerCase()]]?.id;
    if (currency_id) {
      this.saveState = 'progress';
      this.accountService
        .updateAccount({ currency_id })
        .pipe(tap(), takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            location.reload();
            this.saveState = 'default';
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
            this.saveState = 'default';
          },
        });
    } else {
      this.alertVisible = false;
      this.setCurrency(this.myCurrency);
    }
  }

  public skip() {
    this.alertVisible = false;
    this.setCurrency(this.myCurrency);
  }
}
